class SearchResults extends HTMLElement {
  constructor() {
    super();
    this.init();
    this.addEventListeners();
  }
  init = () => {
    this.SELECTORS = {
      suggested: ".suggested button",
      input: ".search-input",
      fetchProducts: ".product-card, product-card",
      productWrapper: ".product-results",
      showMore: ".show-more",
      searchText: ".search-text strong",
      clearButton: ".clear.btn",
      faqResults: ".faq-results",
      faqContent: ".faq-content",
      articles: ".articles",
      productCountText: ".product-count i",
      faqCountText: ".faq-count i",
      resultsCount: ".results_count",
      bestSelling: ".best-selling-container",
      fetchBestSelling: ".best-selling > product-card",
      faqCarosel: "carosel-component.faq-carosel",
      bestSellingCarosel: "carosel-component.best-selling-carosel",
    };
    this.productCount = this.dataset.productCount ?? 6;
    this.page = 1;
    this.requests = 0;
    this.hasFAQ = true;
    this.hasProducts = true;
    this.setBestSelling = false;

    this.suggested = [...this.querySelectorAll(this.SELECTORS.suggested)];
    this.input = this.querySelector(this.SELECTORS.input);
    this.productWrapper = this.querySelector(this.SELECTORS.productWrapper);
    this.showMore = this.querySelector(this.SELECTORS.showMore);
    this.searchText = this.querySelector(this.SELECTORS.searchText);
    this.clearButton = this.querySelector(this.SELECTORS.clearButton);
    this.faqResults = this.querySelector(this.SELECTORS.faqResults);
    this.faqCountText = this.querySelector(this.SELECTORS.faqCountText);
    this.productCountText = this.querySelector(this.SELECTORS.productCountText);
    this.bestSelling = this.querySelector(this.SELECTORS.bestSelling);
    this.faqCarosel = this.querySelector(this.SELECTORS.faqCarosel);
    this.bestSellingCarosel = this.querySelector(
      this.SELECTORS.bestSellingCarosel
    );
    this.fetchData("", true);
  };

  removeTagsExceptAnchor(text) {
    return text.replace(/<(?!\/?a)([^>]*?)>/g, "");
  }

  addEventListeners = () => {
    this.suggested.map((x) => {
      x.addEventListener("click", () => {
        this.suggest(x);
      });
    });

    // 'input' catches typing, paste, autofill — resets debounce every keystroke
    this.input.addEventListener("input", () => {
      this.searchTimeout();
    });

    // Enter fires search immediately, cancels pending debounce
    this.input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        clearTimeout(this.timeout);
        this.search();
      }
    });

    this.showMore.addEventListener("click", (e) => {
      this.page++;
      this.fetchData(this.input.value);
    });

    this.clearButton.addEventListener("click", () => {
      this.input.value = "";
      this.search(true);
    });
  };

  suggest = (x) => {
    this.input.value = x.textContent;
    this.search();
  };

  getHtml = async (data) => {
    if (data.status !== 200) throw new Error("Error fetching.");
    return new DOMParser().parseFromString(await data.text(), "text/html");
  };

  fetchProducts = async (req, query, refresh) => {
  try {
     //       Clear if refresh
    if (refresh) {
      this.productWrapper.innerHTML = "";
    }

    const localePrefix = window.Shopify.routes.root;

    let finalQuery = query;

    // Step 1: Add space between letters & numbers
    finalQuery = finalQuery.replace(/([a-zA-Z])(\d)/g, "$1 $2");

    // Step 2: Add space between numbers & letters
    finalQuery = finalQuery.replace(/(\d)([a-zA-Z])/g, "$1 $2");

    // Step 3: Allow only letters, numbers, space, hyphen
    finalQuery = finalQuery.replace(/[^\w\s-]/gi, " ");

    // Step 4: Normalize spaces
    finalQuery = finalQuery.replace(/\s+/g, " ").trim();

    const hasNumber = /\d/.test(query);

    // Clean special chars, Normalize spaces, Remove space if no number
    finalQuery = finalQuery.replace(/[^\w\s-]/gi, " ");
    finalQuery = finalQuery.replace(/\s+/g, " ").trim();

    // USE SECTION RENDERING (SPEED BOOST)
    const words = finalQuery.split(" ");

    // version 1: normal (with space logic)
    const spacedQuery = words.join(" OR ");

    // version 2: no-space (for matching "cloudmonster")
    const noSpaceQuery = finalQuery.replace(/\s+/g, "");

    // version 3: quoted phrase splits — fixes "corebra" → finds "Core Bra"
    // Only for single-word queries between 6–15 chars
    let splitQuery = "";
    if (words.length === 1 && finalQuery.length >= 6 && finalQuery.length <= 15) {
      const variants = [];
      const max = finalQuery.length - 1;
      for (let i = 3; i <= max; i++) {
        variants.push(`"${finalQuery.slice(0, i)} ${finalQuery.slice(i)}"`);
      }
      if (variants.length) splitQuery = variants.join(" OR ");
    }

    // Combine all
    const parts = [spacedQuery, noSpaceQuery];
    if (splitQuery) parts.push(splitQuery);
    const searchQuery = [...new Set(parts)].join(" OR ");

    const newUrl = `${localePrefix}search?q=${
      !query
        ? window.Shopify.search_hash
        : `-tag:child_product ${searchQuery}`
    }&page=${this.page}&type=product&options[prefix]=last&section_id=main-search`;

    // Fetch only required section (lighter)
    const response = await fetch(newUrl, req);
    const text = await response.text();

    // Parse minimal DOM (faster)
    const parser = new DOMParser();
    const doc = parser.parseFromString(text, "text/html");

    const cards = doc.querySelectorAll(this.SELECTORS.fetchProducts);

    // Append directly (no heavy map)
    const fragment = document.createDocumentFragment();
    cards.forEach((card) => {
      fragment.appendChild(card.cloneNode(true));
    });

    this.productWrapper.appendChild(fragment);

    // Show/hide load more
    cards.length < this.productCount
      ? this.showMore.classList.add("hide")
      : this.showMore.classList.remove("hide");

    this.hasProducts = !!cards.length;

    // Count update
    const countEl = doc.querySelector(this.SELECTORS.resultsCount);
    this.productCountText.textContent = countEl?.value || "";

    // UI state
    this.input.value
      ? this.classList.add("searching")
      : this.classList.remove("searching");

  } catch (err) {
    console.error(err);
  } finally {
    this.fetchDone();
  }
};

  fetchFAQ = async (req, query) => {
    try {
      const newUrl = `/search?q=${
        !query ? window.Shopify.search_hash : query
      }&page=${this.page}&type=article`;
      const data = await fetch(newUrl, req);
      const html = await this.getHtml(data);
      const articles = html.querySelector(this.SELECTORS.articles);
      const faqContent = [
        ...articles.querySelectorAll(this.SELECTORS.faqContent),
      ];
      faqContent.map((content) => {
        content.innerHTML = this.removeTagsExceptAnchor(content.innerHTML);
      });
      const faqBoxes = articles.querySelectorAll(".faq-box");
      faqBoxes.forEach((el) => el.classList.add("swiper-slide"));
      this.hasFAQ = !!faqContent.length;
      this.faqResults.innerHTML = "";
      this.faqResults.append(...faqBoxes);
      this.faqCarosel.swiper.update();
      this.faqCountText.textContent =
        +html.querySelector(this.SELECTORS.resultsCount)?.value || "";
      if (!this.setBestSelling) {
        this.bestSelling.append(
          ...html.querySelectorAll(this.SELECTORS.fetchBestSelling)
        );
        this.setBestSelling = true;
        this.bestSellingCarosel.swiper.update();
      }
    } catch (err) {
      console.error(err);
    } finally {
      this.fetchDone();
    }
  };

  setLoading = (state) => {
    state
      ? this.classList.add("loading")
      : this.classList.remove("loading");
  };

  fetchDone = () => {
    this.requests--;
    if (this.requests === 0) {
      this.setLoading(false);
      if (this.hasProducts || this.hasFAQ) {
        this.classList.remove("no-results");
      } else {
        this.searchText.textContent = this.input.value;
        this.classList.add("no-results");
      }
    }
  };

  fetchData = (query = "", refresh) => {
    if (refresh) this.page = 1;
    this.setLoading(true);
    const req = {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    };
    this.requests++;
    this.fetchProducts(req, query, refresh);
    if (refresh) {
      this.requests++;
      this.fetchFAQ(req, query);
    }
  };

  search = async (initial = false) => {
    this.fetchData(this.input.value, true);
  };

  // Simplified — no hasDelay param needed anymore
  searchTimeout = () => {
    clearTimeout(this.timeout);

    if (this.input.value === "") {
      this.search(true);
      return;
    }

    // 800ms — fires only after user fully stops typing
    this.timeout = setTimeout(() => {
      this.search();
    }, 800);
  };
}

customElements.define("search-results", SearchResults);